import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import { v4 } from 'uuid'

import hljs from 'highlight.js/lib/core'
import hlxml from 'highlight.js/lib/languages/xml'
import 'highlight.js/styles/github-dark.css'
hljs.registerLanguage('xml', hlxml)

import { authedAxios } from '../../utils'
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Notifications,
} from '@mui/icons-material'

import { WizardContext } from '../../setupWizard/shared/WizardContext'
import useIsMobile from '../../hooks/useIsMobile'
import useAlert from '../../hooks/useAlert'

const PREVIEW_BUTTON_ID = 'startup-crm-lead-button'
const SERVER_CDN_PATH = `${window.location.origin}/api/cdn/lead-import`
const TARGET_ID = 'startup-crm-lead-import-target'

const LeadImportSetup = ({ onDone, state, updateState }) => {
  const [isEditing, setIsEditing] = useState(true)
  const [loading, setLoading] = useState(true)

  const { leadImport: config = {} } = state

  const setConfig = (leadImport) => {
    updateState({ leadImport })
  }

  const setButtonStyles = (key, val) =>
    setConfig({
      ...config,
      buttonConfig: {
        ...config.buttonConfig,
        styles: { ...(config?.buttonConfig?.styles || {}), [key]: val },
      },
    })
  const setButtonLabel = (label) =>
    setConfig({
      ...config,
      buttonConfig: {
        ...config.buttonConfig,
        label,
      },
    })

  const hasInitializedData = useMemo(() => !!state.leadImport, [])

  const [ref, setRef] = useState(null)

  const getConfig = () => {
    return authedAxios.get('/api/lead-import')
  }

  const submitConfig = (inConfig) => {
    return authedAxios.put('/api/lead-import', {
      ...(inConfig || config),
    })
  }

  const onSave = () => {
    submitConfig().then(() => {
      setIsEditing(false)
    })
  }

  const { createGenericErrorAlert } = useAlert()

  useEffect(() => {
    if (hasInitializedData) return setLoading(false)

    getConfig()
      .then((res) => {
        const { leadImportConfig } = res.data

        if (!leadImportConfig) {
          updateState({
            leadImport: { hasWebsite: null, wantsLeadImport: null, code: v4() },
          })

          setLoading(false)
          return
        }

        if (
          leadImportConfig.hasWebsite &&
          leadImportConfig.code &&
          leadImportConfig.wantsLeadImport &&
          leadImportConfig.buttonConfig
        )
          setIsEditing(false)

        updateState({ leadImport: leadImportConfig })
        setLoading(false)
      })
      .catch((e) => {
        createGenericErrorAlert()
      })
  }, [])

  const updateHasWebsite = (bool) => {
    const newConfig = { ...config, hasWebsite: bool }
    setConfig(newConfig)
    submitConfig(newConfig)
  }

  const updateWantsLeadImport = (bool) => {
    const newConfig = { ...config, wantsLeadImport: bool }
    setConfig(newConfig)
    submitConfig(newConfig)
  }

  useEffect(() => {
    if (ref) {
      hljs.highlightAll()
    }
  }, [ref])

  useEffect(() => {
    if (!config?.hasWebsite || !config?.wantsLeadImport || !isEditing) return

    const script = document.createElement('script')

    script.src = `${SERVER_CDN_PATH}/preview-${config.code}`
    script.async = true

    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [config?.hasWebsite, config?.wantsLeadImport, isEditing])

  const getQuestionText = () => {
    if (config.hasWebsite === null)
      return 'Do you have a website that you use to advertise to potential customers?'

    if (config.hasWebsite === false)
      return 'Come back here later when you have a website and are interested in sourcing leads from it into this CRM.'

    if (config.wantsLeadImport === null)
      return 'Would you like potential customers to be able to fill out information on your website and have that information funnel into this CRM?'

    if (config.wantsLeadImport === false)
      return 'Come back here later when you are interested in sourcing leads from your website into this CRM.'
  }

  const getPrimaryButton = () => {
    if (config.hasWebsite === null)
      return { text: 'Yes', onClick: () => updateHasWebsite(true) }

    if (config.hasWebsite === false)
      return onDone ? { text: 'Continue', onClick: () => onDone() } : null

    if (config.wantsLeadImport === null)
      return { text: 'Yes', onClick: () => updateWantsLeadImport(true) }

    if (config.wantsLeadImport === false)
      return onDone ? { text: 'Continue', onClick: () => onDone() } : null
  }

  const getSecondaryButton = () => {
    if (config.hasWebsite === null)
      return { text: 'No', onClick: () => updateHasWebsite(false) }

    if (config.hasWebsite === false)
      return { text: 'I have a website', onClick: () => updateHasWebsite(true) }

    if (config.wantsLeadImport === null)
      return { text: 'No', onClick: () => updateWantsLeadImport(false) }

    if (config.wantsLeadImport === false)
      return {
        text: 'I want to import leads from my website',
        onClick: () => updateWantsLeadImport(true),
      }
  }

  const isMobile = useIsMobile()

  if (loading) return <CircularProgress />

  if (!isEditing) {
    return (
      <Box ref={(el) => setRef(el)}>
        <Typography variant={'h5'} sx={{ mb: 2 }}>
          Your lead importing is set up!
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography>
            New leads will look like this in the notification bell
            <Badge badgeContent={4} color={'primary'} sx={{ ml: 1, mr: 2 }}>
              <Notifications />
            </Badge>
            at the top of the screen.
          </Typography>
        </Box>
        <Typography>
          <b>
            Copy this code and paste it in your website where you'd like our
            button to appear
          </b>
        </Typography>
        <Typography>
          <i>(make sure to get it all)</i>
        </Typography>

        <pre>
          <code className='language-xml hljs' style={{ padding: '24px' }}>
            {`<div style="width: 150px; height: 40px;" id='${TARGET_ID}'></div>\n\n<script\n  src='${SERVER_CDN_PATH}/${config.code}'>\n</script>`}
          </code>
        </pre>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          {onDone && <BackButton />}
          <Box>
            <Button
              variant='outlined'
              endIcon={<Edit />}
              onClick={() => setIsEditing(true)}
            >
              Make changes
            </Button>
            {onDone && (
              <Button sx={{ ml: 1 }} onClick={onDone} variant={'contained'}>
                Continue
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  const primaryButton = getPrimaryButton()
  const secondaryButton = getSecondaryButton()

  const hasSavedConfigBefore = config?.buttonConfig

  return (
    <Box>
      <>
        {(!config.hasWebsite || !config.wantsLeadImport) && (
          <>
            <Typography>{getQuestionText()}</Typography>

            <Box
              sx={{
                mt: 2,
                //  display: 'flex', justifyContent: 'flex-end'
              }}
            >
              {secondaryButton && (
                <Button
                  sx={{ mr: 1 }}
                  variant={'outlined'}
                  onClick={secondaryButton.onClick}
                >
                  {secondaryButton.text}
                </Button>
              )}
              {primaryButton && (
                <Button variant={'contained'} onClick={primaryButton.onClick}>
                  {primaryButton.text}
                </Button>
              )}
            </Box>

            {onDone && (
              <Box
                sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}
              >
                <BackButton />
                <Button onClick={onDone} endIcon={<ChevronRight />}>
                  Skip
                </Button>
              </Box>
            )}
          </>
        )}
        {config.hasWebsite && config.wantsLeadImport && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              <Box>
                <Typography variant={'h5'} sx={{ mb: 1 }}>
                  We're going to generate some code that will place a button on
                  your website.
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  When users click on the button, they'll be prompted to fill
                  out some information about themselves which we'll save in your
                  CRM.
                </Typography>

                <Typography sx={{ mb: 2 }}>
                  We just need a few things from you to help us create this
                  button:
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    mt: -2,
                  }}
                >
                  <Box sx={{ mr: 2, mt: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ mb: 1 }}>
                        <b>What should the button say?</b>
                      </Typography>
                      <TextField
                        label={'Button Label'}
                        value={config?.buttonConfig?.label || 'Request Info'}
                        onChange={(e) => {
                          const { value } = e.target
                          document.getElementById(
                            PREVIEW_BUTTON_ID
                          ).textContent = value
                          setButtonLabel(value)
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography sx={{ mb: 1 }}>
                        <b>What colors should the button be?</b>
                      </Typography>
                      <Box sx={{ display: 'flex' }}>
                        <Box sx={{ mr: 2 }}>
                          <Typography>Text Color</Typography>
                          <input
                            type={'color'}
                            value={
                              config?.buttonConfig?.styles.color || '#FFFFFF'
                            }
                            onChange={(e) => {
                              const { value } = e.target
                              const previewButton =
                                document.getElementById(PREVIEW_BUTTON_ID)
                              if (previewButton)
                                previewButton.style.color = value

                              setButtonStyles('color', value)
                            }}
                          ></input>
                        </Box>
                        <Box>
                          <Typography>Background Color</Typography>
                          <input
                            type={'color'}
                            value={
                              config?.buttonConfig?.styles.backgroundColor ||
                              '#1976d2'
                            }
                            onChange={(e) => {
                              const { value } = e.target
                              const previewButton =
                                document.getElementById(PREVIEW_BUTTON_ID)
                              if (previewButton)
                                previewButton.style.backgroundColor = value
                              setButtonStyles('backgroundColor', value)
                            }}
                          ></input>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      border: `1px solid ${grey[300]}`,
                      width: '400px',
                      height: '400px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      flexShrink: 0,
                      borderRadius: '4px',
                      backgroundColor: grey[50],
                    }}
                  >
                    <Typography
                      sx={{ position: 'absolute', right: '8px', top: '8px' }}
                    >
                      Your site (try me)
                    </Typography>
                    <div id={TARGET_ID}></div>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}
            >
              {onDone ? <BackButton /> : <Box />}
              <Box>
                <Button
                  sx={{ mr: 1 }}
                  variant={'outlined'}
                  onClick={() => updateWantsLeadImport(false)}
                  color={hasSavedConfigBefore ? 'error' : 'primary'}
                >
                  {hasSavedConfigBefore ? 'Disable' : 'Cancel'}
                </Button>
                <Button variant={'contained'} onClick={onSave}>
                  Done
                </Button>
              </Box>
            </Box>
          </>
        )}
      </>
    </Box>
  )
}

const BackButton = () => {
  const { navigateToPrevious } = useContext(WizardContext)

  return (
    <Button startIcon={<ChevronLeft />} onClick={navigateToPrevious}>
      Back
    </Button>
  )
}

export default LeadImportSetup
